import { api, type FeedItem } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { fmtKstShort, fmtKstDate } from '../lib/time';
import { ErrorBox } from './Watchlist';

const TYPE_LABEL: Record<string, string> = {
  new_report: '신규 리포트',
  target_change: '목표가 변경',
  recommendation_change: '의견 변경',
  catalyst: '카탈리스트',
  thesis_change: '논리 변경',
};

export default function FeedPage() {
  const { data, error, loading } = useAsync(() => api.feed(), []);
  if (error) return <ErrorBox msg={error} />;
  if (loading) return <p className="text-slate-500">로딩 중…</p>;
  const items = data?.items ?? [];

  return (
    <section>
      <h1 className="text-2xl font-semibold tracking-tight">변화 피드</h1>
      <p className="mt-1 text-sm text-slate-500">최근 7일 이벤트 (target_change · recommendation_change · catalyst)</p>
      <ul className="mt-6 space-y-3">
        {items.length === 0 && (
          <li className="rounded-md border border-slate-200 bg-white px-4 py-6 text-sm text-slate-500">
            아직 이벤트가 없습니다.
          </li>
        )}
        {items.map((it, i) => (
          <FeedRow key={i} item={it} />
        ))}
      </ul>
    </section>
  );
}

function FeedRow({ item }: { item: FeedItem }) {
  // occurred_at: 보고서 발간일 (YYYY-MM-DD) 또는 catalysts.createdAt (UTC timestamp).
  // collected_at: 시스템 수집 시각 (UTC timestamp). 의미적으로 다르면 둘 다 표시, 같으면 한 번만.
  const isDateOnly = item.occurred_at?.length === 10;
  const occurredFmt = isDateOnly ? fmtKstDate(item.occurred_at) : fmtKstShort(item.occurred_at);
  const collectedFmt =
    item.collected_at && item.collected_at !== item.occurred_at
      ? fmtKstShort(item.collected_at)
      : null;

  return (
    <li className="rounded-md border border-slate-200 bg-white px-4 py-3">
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <span className="rounded bg-slate-100 px-2 py-0.5 font-medium text-slate-700">
          {TYPE_LABEL[item.type] ?? item.type}
        </span>
        <span className="font-medium text-slate-900">{item.symbolName ?? item.symbol}</span>
        <span className="font-mono text-[10px] text-slate-400">{item.symbol}</span>
        <span className="ml-auto flex flex-col items-end leading-tight">
          <span>{occurredFmt}</span>
          {collectedFmt && (
            <span className="text-[10px] text-slate-400">수집 {collectedFmt}</span>
          )}
        </span>
      </div>
      <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-all rounded bg-slate-50 p-2 text-xs text-slate-700">
        {JSON.stringify(item.payload, null, 2)}
      </pre>
    </li>
  );
}
